import { z } from 'zod';

/**
 * 편집 파라미터 — «어떻게 편집할지»를 대화가 아니라 **필드**로 받는다.
 *
 * 설계: `docs/shorts-editor-input-redesign.md` D1·D2·D3
 *
 * 왜 여기(editor-core)인가: 프론트 폼·API 검증·에이전트 제약 생성이 전부 같은
 * 정의를 봐야 한다. 세 곳에 각각 두면 «폼에는 있는데 서버가 무시하는 필드»가
 * 생기고, 그건 사용자에게 «설정했는데 안 먹힌다»로 보인다 — 우리가 지금 고치려는
 * 실패 그 자체다.
 *
 * 왜 EDL 과 분리하나: EDL 은 «결과 문서»(무엇이 놓였나)이고 파라미터는 «의도»
 * (무엇을 원했나)다. 렌더를 다시 돌리거나 씬을 다시 짜도 의도는 살아남아야 한다.
 */

/**
 * 무음 제거 프리셋. 연속 슬라이더가 아니라 이산 3단계다 — Submagic 선례
 * (`natural` 0.6초+ / `fast` 0.2~0.6초 / `extra-fast` 0.1~0.2초).
 * 실제 판정은 **파형 기반**이며 전사 기반이 아니다(D5). 여기서는 «얼마나 공격적으로»
 * 만 고른다.
 */
export const SILENCE_MODES = ['off', 'natural', 'fast', 'extra-fast'] as const;
export type SilenceMode = (typeof SILENCE_MODES)[number];

/** 프리셋별 «이보다 긴 무음만 자른다» 임계(초). 구현(#5)과 UI 설명이 같은 값을 본다. */
export const SILENCE_THRESHOLD_SEC: Record<Exclude<SilenceMode, 'off'>, number> = {
  natural: 0.6,
  fast: 0.3,
  'extra-fast': 0.15,
};

/**
 * 길이 하한/상한. 쇼츠 플랫폼 상한(YouTube Shorts 180초)과, 그보다 짧으면
 * 편집이랄 게 없어지는 지점(3초)을 양 끝으로 잡는다. 대화에서 값을 뽑던
 * `extractActiveConstraints` 와 **같은 범위**여야 한다 — 다르면 "채팅으론 되는데
 * 폼으론 안 되는" 차이가 생긴다.
 */
export const TARGET_DURATION_MIN_SEC = 3;
export const TARGET_DURATION_MAX_SEC = 180;
export const SCENE_COUNT_MIN = 1;
export const SCENE_COUNT_MAX = 20;

/**
 * 자동 모드 — AI 가 길이·씬 수를 알아서 정한다. 스칼라 필드는 **받지 않는다**.
 *
 * D3: "AI가 알아서 + 근데 이건 이렇게"라는 중간 상태를 만들지 않는다. Submagic 이
 * 템플릿을 주면 다른 필드를 아예 거부하는 것과 같은 이유다. 중간 상태는 사용자가
 * 같은 지시를 네 번 반복하게 만든 실패의 원인이다(§2.1).
 */
const autoParamsSchema = z.object({
  mode: z.literal('auto'),
  captions: z.boolean().default(true),
  headline: z.boolean().default(false),
  removeSilence: z.enum(SILENCE_MODES).default('off'),
});

/** 수동 모드 — 길이·씬 수를 사용자가 못 박는다. */
const manualParamsSchema = z.object({
  mode: z.literal('manual'),
  /** 총 길이(초). 생략하면 «길이는 알아서» — 씬 수만 고정하는 경우다. */
  targetDurationSec: z
    .number()
    .min(TARGET_DURATION_MIN_SEC)
    .max(TARGET_DURATION_MAX_SEC)
    .nullable()
    .default(null),
  /** video-1 위 클립 개수. 생략하면 «씬 수는 알아서». */
  sceneCount: z.number().int().min(SCENE_COUNT_MIN).max(SCENE_COUNT_MAX).nullable().default(null),
  captions: z.boolean().default(true),
  headline: z.boolean().default(false),
  removeSilence: z.enum(SILENCE_MODES).default('off'),
});

/**
 * 판별 유니온이라 «auto 인데 targetDurationSec 이 있는» 요청은 파싱 단계에서 죽는다.
 * 이것이 D3 를 규약이 아니라 **타입**으로 만드는 지점이다.
 */
export const shortsParamsSchema = z.discriminatedUnion('mode', [
  autoParamsSchema,
  manualParamsSchema,
]);

export type ShortsParams = z.infer<typeof shortsParamsSchema>;

/** 새 프로젝트의 기본값 — 지금까지의 동작(자막 켬, 나머지 끔)과 같다. */
export const DEFAULT_SHORTS_PARAMS: ShortsParams = {
  mode: 'auto',
  captions: true,
  headline: false,
  removeSilence: 'off',
};

/**
 * 파라미터를 에이전트용 제약 문장으로 바꾼다.
 *
 * 왜 문자열인가: 에이전트 컨텍스트가 텍스트라서다. 대신 **대화에서 추출하지 않는다** —
 * 값의 출처가 필드 하나뿐이어야 "3턴 전에 말한 15초"가 소실될 수 없다.
 *
 * 자동 모드는 빈 배열이다. 자동인데 제약을 얹으면 그게 바로 D3 가 금지한 중간 상태다.
 */
export function paramsToConstraints(params: ShortsParams): string[] {
  const out: string[] = [];
  if (params.mode === 'manual') {
    if (params.targetDurationSec !== null) {
      out.push(
        `target length: ${params.targetDurationSec}s (exact — set by the user in the form, this outranks the 18-28s default)`
      );
    }
    if (params.sceneCount !== null) {
      out.push(
        `number of clips on video-1: exactly ${params.sceneCount} (set by the user in the form)`
      );
    }
  }
  // 자막·제목은 모드와 무관한 on/off 다 — 자동 모드에서도 사용자가 끄면 꺼야 한다.
  // (D3 가 금지한 것은 «자동인데 스칼라를 못 박는» 중간 상태지, on/off 토글이 아니다.)
  //
  // 자막은 실제로는 서버가 전사에서 깔기 때문에 모델이 손댈 일이 없다. 그래도 알려주는
  // 이유는 모델이 "자막도 넣어드릴게요" 같은 **거짓 약속**을 하지 않게 하기 위해서다.
  out.push(
    params.captions
      ? 'captions: ON (generated from the transcript automatically — you never write or time them)'
      : 'captions: OFF (the user turned subtitles off — do not promise or mention adding them)'
  );
  out.push(
    params.headline
      ? 'headline: ON — the short needs ONE short title overlay (setTitle)'
      : 'headline: OFF — do not add a title overlay'
  );
  return out;
}

/**
 * 완성본에 **실제로 무엇이 적용됐는지** 요약.
 *
 * 🔴 왜 `ShortsParams` 만으로는 부족한가: 설정 값(길이·자막)은 매번 거의 같은데,
 * 정작 비교하고 싶은 것(훅을 켰나, 줌이 들어갔나)은 **동작으로 적용**되는 것이라
 * 설정에 안 남는다. 검수 원문 —
 * "내가 비교하고 싶었던 건 **훅 켰나 껐나, 줌이 들어갔나**다. … 안 바꾸는 값을 보여주고
 * **바꾸는 값을 안 보여준다.**"
 * "훅 켠 편과 끈 편이 목록 배지에 구분돼 남아야 한다. 안 남으면 내가 3편 켜고 3편 꺼도
 * 비교할 방법이 없고, 그러면 «실물로 확인한 적 없다»는 상태가 **영원히 안 끝난다.**"
 *
 * 그래서 발행 시점의 EDL 에서 직접 센다 — 설정이 아니라 **결과**를 본다.
 */
export interface EditSummary {
  /** 화면을 당긴 횟수. */
  zoomCount: number;
  /** 얼굴(웹캠) PIP 이 얹혀 있는가. */
  webcamPip: boolean;
  /** 효과음 개수. */
  sfxCount: number;
  /** 영상 컷 수. 콜드오픈을 붙이면 하나 늘어난다. */
  clipCount: number;
  /** 자막 줄 수. */
  captionCount: number;
}

/**
 * EDL 에서 «무엇이 적용됐나»를 센다.
 *
 * 줌 판정은 `zoom.ts` 와 **같은 원리**다 — 절대값이 아니라 «같은 소스 클립 중 가장 넓은
 * 크롭»보다 좁은 것을 줌으로 본다. 사람이 잡아 둔 프레이밍을 줌으로 세지 않기 위해서다.
 */
export function summarizeEdits(edl: {
  tracks: {
    kind: string;
    items: { type: string; crop?: { w: number } }[];
  }[];
}): EditSummary {
  const videoItems = edl.tracks
    .filter((t) => t.kind === 'video')
    .flatMap((t) => t.items)
    .filter((i) => i.type === 'clip');
  const widest = videoItems.reduce((m, i) => Math.max(m, i.crop?.w ?? 1), 0.0001);
  return {
    zoomCount: videoItems.filter((i) => (i.crop?.w ?? 1) < widest - 0.01).length,
    webcamPip: edl.tracks.some((t) => t.kind === 'broll' && t.items.length > 0),
    sfxCount: edl.tracks.filter((t) => t.kind === 'sfx').reduce((n, t) => n + t.items.length, 0),
    clipCount: videoItems.length,
    captionCount: edl.tracks
      .filter((t) => t.kind === 'caption')
      .reduce((n, t) => n + t.items.length, 0),
  };
}
